/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useEffect } from 'react'
import { css } from '@emotion/react'
import { Collapse } from 'antd'
import { TextInput } from '../ts-components/TextInput'
import Button from '../ts-components/Button'
import colors from '@sportninja/common/constants/appColors'
import { FormItem } from '../../types/CustomForm'
import { AnswerAction, AnswerState } from './index'
import { req } from '@sportninja/common/api/request'

interface HockeyUsaFieldProps {
  item: FormItem
  commonProps: any
  answers: AnswerState
  dispatchAnswer: (action: AnswerAction) => void
  disabledFields?: Set<string>
  variant?: 'default' | 'modal'
  firstName?: string
  lastName?: string
  scheduleId?: string
  isReadOnly?: boolean
}

/**
 * USA Hockey field component with validation
 */
const HockeyUsaField: React.FC<HockeyUsaFieldProps> = ({
  item,
  commonProps,
  answers,
  dispatchAnswer,
  disabledFields,
  variant = 'default',
  firstName = '',
  lastName = '',
  scheduleId,
  isReadOnly = false,
}) => {
  const [validationStatus, setValidationStatus] = useState<
    'pending' | 'success' | 'error'
  >(isReadOnly ? 'success' : 'pending')
  const [isLoading, setIsLoading] = useState(false)
  const [validationResult, setValidationResult] = useState<any>(null)
  const [isCollapseExpanded, setIsCollapseExpanded] = useState(isReadOnly)
  const [hideFormError, setHideFormError] = useState(false)
  const currentValue = String(answers[item.id] ?? '')

  // Parse backend error messages - check both commonProps.name and item.id based keys
  const fieldErrorKey = commonProps.name || `form.${item.id}`
  const errorMessage =
    commonProps.fieldErrors?.[fieldErrorKey] ||
    commonProps.fieldErrors?.[item.id] ||
    commonProps.customError?.[fieldErrorKey] ||
    commonProps.customError?.[item.id]

  const parsedError = useMemo(() => {
    if (!errorMessage || typeof errorMessage !== 'string') {
      return null
    }
    try {
      const parsed = JSON.parse(errorMessage)
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed
      }
    } catch (e) {
      // Not a JSON string, return null
    }
    return null
  }, [errorMessage])

  const isObjectError = parsedError !== null

  // When a new error message arrives from form submission, stop hiding it and clear validation result
  useEffect(() => {
    if (errorMessage) {
      setHideFormError(false)
      // Clear the validation button result when a new form error arrives
      setValidationResult(null)
      setValidationStatus('pending')
      // Expand collapse to show the new form error
      if (parsedError) {
        setIsCollapseExpanded(true)
      }
    }
  }, [errorMessage, parsedError])

  // Intelligent success detection: check for new 'success' field, fallback to 'status'
  const isBackendSuccess = useMemo(() => {
    if (!isObjectError || !parsedError) {
      return false
    }

    // Check for new API response format with 'success' field
    if (parsedError.success !== undefined) {
      // Handle both string and boolean success values
      return (
        parsedError.success === true ||
        parsedError.success === 'true' ||
        parsedError.success === 'True'
      )
    }

    // Fallback to legacy 'status' field for backward compatibility
    return parsedError.status === 'Success'
  }, [isObjectError, parsedError])

  // Check if API validation result indicates success
  const isApiValidationSuccess = useMemo(() => {
    if (!validationResult) {
      return false
    }

    // Check for success field in API response
    if (validationResult.success !== undefined) {
      return (
        validationResult.success === true ||
        validationResult.success === 'true' ||
        validationResult.success === 'True'
      )
    }

    // Fallback to status field
    return validationResult.status === 'Success'
  }, [validationResult])

  // Combine isObjectError and hideFormError to decide if form error should be displayed
  const displayFormError = isObjectError && !hideFormError

  // Prepare TextInput props, removing object error from fieldErrors and customError
  const textInputProps = useMemo(() => {
    // Always clean object errors to prevent them from showing as JSON strings
    if (isObjectError) {
      // Determine if fieldErrors is an array or object and clean accordingly
      let cleanedFieldErrors = commonProps.fieldErrors
      let cleanedCustomError = commonProps.customError

      if (Array.isArray(commonProps.fieldErrors)) {
        // If it's an array, keep it as an empty array
        cleanedFieldErrors = []
      } else if (commonProps.fieldErrors) {
        // If it's an object, remove the error keys
        cleanedFieldErrors = { ...commonProps.fieldErrors }
        delete cleanedFieldErrors[fieldErrorKey]
        delete cleanedFieldErrors[item.id]
      } else {
        // If it's null/undefined, default to empty array for safety
        cleanedFieldErrors = []
      }

      // Clean customError object
      if (commonProps.customError) {
        cleanedCustomError = { ...commonProps.customError }
        delete cleanedCustomError[fieldErrorKey]
        delete cleanedCustomError[item.id]
      } else {
        cleanedCustomError = {}
      }

      return {
        ...commonProps,
        fieldErrors: cleanedFieldErrors,
        customError: cleanedCustomError,
      }
    }
    return commonProps
  }, [commonProps, isObjectError, fieldErrorKey, item.id])

  const handleValidate = async () => {
    console.log('🔄 USA Hockey validation triggered for:', currentValue)

    // Hide form errors to prioritize validation button results
    setHideFormError(true)

    setIsLoading(true)
    setValidationResult(null)

    try {
      const payload = {
        name_first: firstName,
        name_last: lastName,
        confirmation_number: currentValue.trim(),
      }

      console.log(
        '🔄 handleValidate: Making API request with payload:',
        payload
      )

      const response = await req(
        `/registration/${scheduleId}/integrations/usahockey/validate`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      )

      console.log('✅ handleValidate: API response:', response)
      setValidationResult(response)

      // Set validation status based on API response
      const isSuccess =
        response.success === true ||
        response.success === 'true' ||
        response.success === 'True' ||
        response.status === 'Success'
      setValidationStatus(isSuccess ? 'success' : 'error')

      // Expand the collapse to show results
      setIsCollapseExpanded(true)
    } catch (error) {
      console.error('🚨 handleValidate: API error:', error)
      setValidationStatus('error')
      setValidationResult({
        status: 'Error',
        message: error instanceof Error ? error.message : 'Validation failed',
        data: {},
      })

      // Expand the collapse to show error results
      setIsCollapseExpanded(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Emotion CSS styles for USA Hockey component
  const HockeyUsaContainer = css`
    display: flex;
    gap: 16px;
    width: 100%;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  `

  const HockeyUsaInputSection = css`
    flex: 1;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    @media (max-width: 768px) {
      flex-direction: column;
    }
  `

  const HockeyUsaResultsSection = css`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    flex-direction: column;
  `

  const HockeyUsaCollapse = css`
    background-color: ${variant === 'modal'
      ? colors.SECONDARY_200
      : colors.NEUTRAL_800};
    border: 1px solid
      ${variant === 'modal' ? colors.SECONDARY_400 : colors.NEUTRAL_600};
    border-radius: 8px;
    width: 100%;
    align-self: flex-end;
    margin-top: 24px;

    .ant-collapse-header {
      background-color: ${variant === 'modal'
        ? colors.SECONDARY_400
        : colors.NEUTRAL_700} !important;
      border-bottom: 1px solid
        ${variant === 'modal' ? colors.SECONDARY_200 : colors.NEUTRAL_600};
      padding: 12px 16px !important;
      color: ${colors.NEUTRAL_0} !important;
      font-weight: 600;
      font-size: 14px;
      border-radius: 8px 8px 0 0 !important;
    }

    .ant-collapse-content {
      background-color: ${variant === 'modal'
        ? colors.SECONDARY_200
        : colors.NEUTRAL_800};
      border-top: none;
      border-radius: 0 0 8px 8px !important;
    }

    .ant-collapse-content-box {
      padding: 8px 16px;
    }

    .ant-collapse-expand-icon {
      color: ${colors.NEUTRAL_0} !important;
    }
  `

  const HockeyUsaCollapseHeader = css`
    color: ${colors.NEUTRAL_0};
    font-weight: 600;
    font-size: 14px;
  `

  const HockeyUsaResults = css`
    display: flex;
    flex-direction: column;
    gap: 2px;
  `

  const HockeyUsaResultItem = css`
    color: ${colors.NEUTRAL_0};
    font-size: 14px;
    display: flex;

    strong {
      color: ${colors.NEUTRAL_100};
      margin-right: 8px;
      min-width: 140px;
    }
  `

  const HockeyUsaPlaceholder = css`
    color: ${colors.NEUTRAL_200};
    font-style: italic;
    text-align: center;
    padding: 24px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60px;
    background-color: ${variant === 'modal'
      ? colors.SECONDARY_200
      : colors.NEUTRAL_800};
    border: 1px solid
      ${variant === 'modal' ? colors.SECONDARY_400 : colors.NEUTRAL_600};
    border-radius: 8px;
    width: 100%;
    align-self: flex-end;
  `

  const HockeyUsaSuccessStatus = css`
    color: ${colors.SUCCESS_300};
    font-weight: 600;
  `

  const HockeyUsaErrorStatus = css`
    color: ${colors.ERROR_300};
    font-weight: 600;
  `

  return (
    <div css={HockeyUsaContainer}>
      {/* Left half - Input and validation */}
      <div css={HockeyUsaInputSection}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            align-self: stretch;
            flex: 1;
          `}
        >
          <TextInput
            {...textInputProps}
            value={currentValue}
            setValue={(v) => {
              dispatchAnswer({ type: 'SET', id: item.id, value: v })
              // Show form errors again when user starts typing
              setHideFormError(false)
              // Reset validation when user changes input
              if (validationResult) {
                setValidationResult(null)
                setValidationStatus('pending')
                setIsCollapseExpanded(false)
              }
            }}
            placeholder='Enter USA Hockey Confirmation Number'
            disabled={
              isReadOnly ||
              disabledFields?.has(item.id) ||
              (validationResult && isApiValidationSuccess) ||
              (displayFormError && isBackendSuccess)
            }
            success={
              isReadOnly ||
              (validationResult && isApiValidationSuccess) ||
              (displayFormError && isBackendSuccess)
            }
            error={
              !isReadOnly &&
              ((validationResult && !isApiValidationSuccess) ||
                (displayFormError && !isBackendSuccess))
            }
          />
        </div>
        <div
          css={css`
            margin-top: 28px;
            @media (max-width: 768px) {
              margin-top: 8px;
              width: 100%;
            }
          `}
        >
          <Button
            variant='primary'
            text={isLoading ? 'Validating...' : 'Validate'}
            onClick={handleValidate}
            disabled={
              isReadOnly ||
              !currentValue.trim() ||
              isLoading ||
              (validationResult && isApiValidationSuccess) ||
              (displayFormError && isBackendSuccess)
            }
            style={{ minWidth: '120px', width: '100%' }}
          />
        </div>
      </div>

      {/* Right half - Results collapse */}
      <div css={HockeyUsaResultsSection}>
        {isReadOnly ||
        validationStatus !== 'pending' ||
        displayFormError ||
        validationResult ? (
          <Collapse
            css={HockeyUsaCollapse}
            activeKey={isCollapseExpanded ? ['1'] : []}
            onChange={(keys) => setIsCollapseExpanded(keys.length > 0)}
            items={[
              {
                key: '1',
                label: (
                  <span css={HockeyUsaCollapseHeader}>
                    USA Hockey Response -{' '}
                    <span
                      css={
                        isReadOnly ||
                        (displayFormError && isBackendSuccess) ||
                        (validationResult && isApiValidationSuccess)
                          ? HockeyUsaSuccessStatus
                          : HockeyUsaErrorStatus
                      }
                    >
                      {isReadOnly ||
                      (displayFormError && isBackendSuccess) ||
                      (validationResult && isApiValidationSuccess)
                        ? 'Success'
                        : 'Error'}
                    </span>
                  </span>
                ),
                children: (
                  <div css={HockeyUsaResults}>
                    {isReadOnly ? (
                      // Show success message for read-only mode
                      <>
                        <div css={HockeyUsaResultItem}>
                          <strong>Status:</strong>
                          <span>Validated</span>
                        </div>
                        <div css={HockeyUsaResultItem}>
                          <strong>Confirmation Number:</strong>
                          <span>{currentValue}</span>
                        </div>
                      </>
                    ) : displayFormError ? (
                      // Display backend API response content
                      <>
                        {/* Display user-facing message if available */}
                        {parsedError.message && (
                          <div css={HockeyUsaResultItem}>
                            <strong>Message:</strong>
                            <span>{parsedError.message}</span>
                          </div>
                        )}

                        {/* Display API message as details if available */}
                        {parsedError.api_message && (
                          <div css={HockeyUsaResultItem}>
                            <strong>Details:</strong>
                            <span>{parsedError.api_message}</span>
                          </div>
                        )}

                        {/* Fallback to status field for legacy responses */}
                        {!parsedError.api_message && parsedError.status && (
                          <div css={HockeyUsaResultItem}>
                            <strong>Details:</strong>
                            <span>{parsedError.status}</span>
                          </div>
                        )}

                        {/* Display all fields from the data object */}
                        {parsedError.data && (
                          <>
                            {Object.entries(parsedError.data).map(
                              ([key, value]) => {
                                // Format the key for display (convert snake_case to Title Case)
                                const formattedKey = key
                                  .split('_')
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(' ')

                                // Convert value to string for display, handle null/undefined
                                const displayValue =
                                  value != null ? String(value) : 'N/A'

                                return (
                                  <div key={key} css={HockeyUsaResultItem}>
                                    <strong>{formattedKey}:</strong>
                                    <span>{displayValue}</span>
                                  </div>
                                )
                              }
                            )}
                          </>
                        )}
                      </>
                    ) : validationResult ? (
                      // Display API validation result
                      <>
                        {validationResult.message && (
                          <div css={HockeyUsaResultItem}>
                            <strong>Message:</strong>
                            <span>{validationResult.message}</span>
                          </div>
                        )}
                        {validationResult.data && (
                          <>
                            {Object.entries(validationResult.data).map(
                              ([key, value]) => {
                                // Format the key for display (convert snake_case to Title Case)
                                const formattedKey = key
                                  .split('_')
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(' ')

                                // Convert value to string for display, handle null/undefined
                                const displayValue =
                                  value != null ? String(value) : 'N/A'

                                return (
                                  <div key={key} css={HockeyUsaResultItem}>
                                    <strong>{formattedKey}:</strong>
                                    <span>{displayValue}</span>
                                  </div>
                                )
                              }
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div css={HockeyUsaResultItem}>
                          <strong>Message:</strong>
                          <span>Validation failed</span>
                        </div>
                        <div css={HockeyUsaResultItem}>
                          <strong>Details:</strong>
                          <span>
                            Please check the information and try again
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <div css={HockeyUsaPlaceholder}>
            Enter and validate a USA Hockey Confirmation Number to see results
          </div>
        )}
      </div>
    </div>
  )
}

export default HockeyUsaField
